import { useState } from 'react'
import { useFetcher, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { isDemoProperty } from '#app/components/application-detail-v2/demo/demo-hooks'
import {
	ReachOutDialog,
	type ReachOutMember,
} from '#app/components/application-detail-v2/vendor/ReachOutDialog'
import { RenewCreditsDialog } from '#app/components/billing/RenewCreditsDialog'
import { deriveReportAction } from '#app/utils/application-detail-v2/derive'
import { applicationTypeLabel } from '#app/utils/application-detail-v2/display'
import type { ApplicationDetailData } from '#app/utils/application-detail-v2/types'
import {
	ApplicationDetailPage,
	type DetailPageHandlers,
} from './ApplicationDetailPage'
/**
 * Step 4b interaction layer: owns WHICH dialog is open and the downloads
 * hook, and hands typed handlers to the static 4a composition. Each dialog
 * owns its OWN useFetcher posting `{ intent, ...fields }` to the v2 route
 * action (per-dialog fetcher ownership); visibility of every entry point is
 * enforced upstream by `can.*` + the derived-status action map (header).
 * The upsell CTA only toasts — dormant until TT-BILL-200.
 */
import { HeaderProfileMenu } from './HeaderProfileMenu'
import { AddApplicantDialog } from './dialogs/AddApplicantDialog'
import { CreditPreviewDialog } from './dialogs/CreditPreviewDialog'
import { DecisionDialog, type DecisionIntent } from './dialogs/DecisionDialog'
import { DeleteApplicationDialog } from './dialogs/DeleteApplicationDialog'
import { IdVerificationModal } from './dialogs/IdVerificationModal'
import { IncomeVerificationModal } from './dialogs/IncomeVerificationModal'
import { ProceedToFullScreenDialog } from './dialogs/ProceedToFullScreenDialog'
import { RegenerateDialog } from './dialogs/RegenerateDialog'
import { ReinstateDialog } from './dialogs/ReinstateDialog'
import { RentVerificationModal } from './dialogs/RentVerificationModal'
import { RequestBankDialog } from './dialogs/RequestBankDialog'
import { RequestDocumentsDialog } from './dialogs/RequestDocumentsDialog'
import { RequestGuarantorDialog } from './dialogs/RequestGuarantorDialog'
import { SendUpdateLinkDialog } from './dialogs/SendUpdateLinkDialog'
import { TimelinePanel } from './dialogs/TimelinePanel'
import {
	IncomeVerificationModalV2,
	RentVerificationModalV2,
} from './dialogs/verification-report-v2'
import { VERIFICATION_REPORT_UI_VERSION } from './dialogs/verification-ui-version'
import { useDownloads } from './downloads/useDownloads'

const useVerificationReportV2 = VERIFICATION_REPORT_UI_VERSION === 'v2'
const IncomeReportModal = useVerificationReportV2
	? IncomeVerificationModalV2
	: IncomeVerificationModal
const RentReportModal = useVerificationReportV2
	? RentVerificationModalV2
	: RentVerificationModal

type DialogKind =
	| DecisionIntent
	| 'addApplicant'
	| 'guarantor'
	| 'bank'
	| 'requestDocuments'
	| 'reachOut'
	| 'sendLink'
	| 'timeline'
	| 'id'
	| 'income'
	| 'rent'
	| 'credit'
	| 'regenerate'
	| 'fullScreen'
	| 'delete'
	| 'reinstate'
	| 'topup'

// The SAME DecisionDialog the combined page uses — approve/decline render
// the per-row Accept/Decline pills (2026-07-16 redesign); withdraw keeps
// the plain confirm. Abandon retired as an offered action (owner
// 2026-07-17) — existing abandoned rows still display, and
// scripts/migrate-abandoned-applications.ts moves them off the status.
const DECISION_INTENTS: DecisionIntent[] = ['approve', 'decline', 'withdraw']

export function DetailPageController({
	data,
}: {
	data: ApplicationDetailData
}) {
	// Deep-link: the dashboard Action Center's "View Timeline" links here with
	// `?view=timeline`, so open the timeline dialog on arrival (render-time
	// init, no useEffect). The param is stripped when the dialog closes.
	const [searchParams, setSearchParams] = useSearchParams()
	const [dialog, setDialog] = useState<DialogKind | null>(
		searchParams.get('view') === 'timeline' ? 'timeline' : null,
	)
	/** Independent of `dialog` so guarantor confirm can open top-up without
	 * losing the pending send. */
	const [topupOpen, setTopupOpen] = useState(false)
	const clearViewParam = () => {
		if (!searchParams.has('view')) return
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.delete('view')
				return next
			},
			{ replace: true },
		)
	}
	const downloadsApi = useDownloads({
		application: data.application,
		downloads: data.downloads,
		credit: data.credit,
		veriffDocuments: data.veriffDocuments,
	})

	const openChange = (kind: DialogKind) => (open: boolean) =>
		setDialog(open ? kind : null)
	const aiReportData = data.results?.aiReportData ?? null
	// Drives the (re)generate dialog's copy: 'generate' (failed, nothing to
	// replace) vs 'regenerate' (replaces a finished report). Same gate as the
	// header kebab + failed-card CTA that open this dialog.
	const reportAction = deriveReportAction({
		canRegenerate: data.canRegenerate,
		derivedStatus: data.application.derivedStatus,
		aiProcessingStatus: data.results?.aiProcessingStatus,
	})

	// TT-12 billing-held resume — posts straight to the resource (no dialog);
	// the endpoint re-resolves the screening type and re-checks the wallet, so
	// a still-low balance surfaces as an inline error on the held card.
	const resumeHoldFetcher = useFetcher<{ error?: string }>()

	// Reach Out rows — the dashboard dialog's member shape, built from the
	// household (solo apps fall back to the entry applicant). Members without
	// a phone stay listed; the dialog itself disables them.
	const reachOutMembers = (
		data.groupMembers.length > 0
			? data.groupMembers.map((member) => ({
					key: member.applicationId,
					fullName: member.displayName,
					shortName: member.displayName,
					roleLabel: member.relationshipLabel,
					phone: member.phone ?? null,
				}))
			: [
					{
						key: data.application.id,
						fullName: data.application.applicantName,
						shortName: data.application.applicantName,
						roleLabel: applicationTypeLabel(data.application.applicationType),
						phone: data.application.applicantPhone,
					},
				]
	) satisfies ReachOutMember[]

	const handlers: DetailPageHandlers = {
		onApprove: () => setDialog('approve'),
		onDecline: () => setDialog('decline'),
		onWithdraw: () => setDialog('withdraw'),
		onReinstate: () => setDialog('reinstate'),
		onAddApplicant: () => setDialog('addApplicant'),
		onRequestGuarantor: () => setDialog('guarantor'),
		onRequestBankAccount: () => setDialog('bank'),
		onRequestDocuments: () => setDialog('requestDocuments'),
		onReachOut: () => setDialog('reachOut'),
		// "Send Update Link" — emails the applicant a resume link (the old
		// fail-closed `share` intent was removed; this is the single
		// resume-link action, V2-GAPS-AND-PDF-PLAN §B).
		onSendUpdateLink: () => setDialog('sendLink'),
		onDeleteApplication: () => setDialog('delete'),
		onOpenTimeline: () => setDialog('timeline'),
		onRegenerateReport: () => setDialog('regenerate'),
		// Opens the confirm-and-charge dialog when an upgrade is actually
		// available (completed pre-screen report + org entitled to full screening);
		// otherwise the offer isn't actionable yet (report still processing, or the
		// plan doesn't include full screening).
		onProceedToFullScreen: () =>
			data.fullScreenUpgrade
				? setDialog('fullScreen')
				: toast.info(
						"Full screening isn't available for this application right now.",
					),
		onViewIdVerification: () => setDialog('id'),
		onViewIncomeVerification: () => setDialog('income'),
		onViewRentVerification: () => setDialog('rent'),
		// Credit "View" opens the PDF-preview dialog (with its own Download
		// button) like the other tiles, instead of force-downloading.
		onViewCreditReport: () => setDialog('credit'),
		// Credit top-up — only when Excel purchase_credits is held (Owner + PM).
		// Credit top-up button now lives in the header action row (before the
		// Download menu) instead of a standalone top strip. Independent state
		// so Request Guarantor confirm can open it without dismissing.
		onOpenTopup: data.billing?.canPurchaseCredits
			? () => setTopupOpen(true)
			: undefined,
	}

	return (
		<>
			<ApplicationDetailPage
				data={data}
				handlers={handlers}
				downloadsApi={downloadsApi}
				// The route hides the shared SiteHeader, so the account menu is
				// rendered HERE (inside the data router) and slotted into the
				// page's own top bar.
				profileMenu={<HeaderProfileMenu />}
				resumeHold={
					data.application.billingHold
						? {
								onGenerate: () =>
									void resumeHoldFetcher.submit(
										{ applicationId: data.application.id },
										{
											method: 'POST',
											action: '/resources/screening-resume-hold',
										},
									),
								pending: resumeHoldFetcher.state !== 'idle',
								error: resumeHoldFetcher.data?.error,
							}
						: undefined
				}
			/>

			{DECISION_INTENTS.map((intent) => (
				<DecisionDialog
					key={intent}
					intent={intent}
					open={dialog === intent}
					onOpenChange={openChange(intent)}
					application={data.application}
					groupMembers={data.groupMembers}
					pendingInvitations={data.pendingInvitations}
					property={data.property}
					memberTimelines={data.memberTimelines}
					screeningKind={data.screeningKind}
				/>
			))}

			<RegenerateDialog
				open={dialog === 'regenerate'}
				onOpenChange={openChange('regenerate')}
				application={data.application}
				mode={reportAction ?? 'regenerate'}
			/>
			{data.fullScreenUpgrade ? (
				<ProceedToFullScreenDialog
					open={dialog === 'fullScreen'}
					onOpenChange={openChange('fullScreen')}
					application={data.application}
				/>
			) : null}
			{/* Same shared dialog and `addApplicant` intent the combined page
			    mounts. It posts with no `action`, so it targets THIS route — whose
			    action is dispatchDetailAction, the same dispatcher the combined
			    route uses. Nothing route-specific is needed. */}
			<AddApplicantDialog
				open={dialog === 'addApplicant'}
				onOpenChange={openChange('addApplicant')}
				application={data.application}
			/>
			<RequestGuarantorDialog
				open={dialog === 'guarantor'}
				onOpenChange={openChange('guarantor')}
				application={data.application}
				propertyStep={data.steps.property}
				property={data.property}
				upgradeHouseholdMembers={data.groupMembers}
				guarantorUpgradeEnabled={data.applicationCategory !== 'student_housing'}
				willUpgradeOnGuarantorComplete={
					data.screeningKind === 'pre_screening' &&
					data.applicationCategory !== 'student_housing'
				}
				walletBalance={data.billing?.balance ?? null}
				upgradeCreditsPerApplicant={data.fullScreenUpgradeCredits}
				onTopUp={data.billing ? () => setTopupOpen(true) : undefined}
			/>
			<RequestBankDialog
				open={dialog === 'bank'}
				onOpenChange={openChange('bank')}
				application={data.application}
				groupMembers={data.groupMembers}
			/>
			<RequestDocumentsDialog
				open={dialog === 'requestDocuments'}
				onOpenChange={openChange('requestDocuments')}
				application={data.application}
				groupMembers={data.groupMembers}
			/>
			<ReachOutDialog
				open={dialog === 'reachOut'}
				onOpenChange={openChange('reachOut')}
				members={reachOutMembers}
			/>
			<SendUpdateLinkDialog
				open={dialog === 'sendLink'}
				onOpenChange={openChange('sendLink')}
				application={data.application}
				groupMembers={data.groupMembers}
			/>
			<DeleteApplicationDialog
				open={dialog === 'delete'}
				onOpenChange={openChange('delete')}
				application={data.application}
			/>
			<ReinstateDialog
				open={dialog === 'reinstate'}
				onOpenChange={openChange('reinstate')}
				application={data.application}
			/>
			<TimelinePanel
				open={dialog === 'timeline'}
				onOpenChange={(open) => {
					setDialog(open ? 'timeline' : null)
					if (!open) clearViewParam()
				}}
				header={{
					bedrooms: data.property.bedrooms,
					moveInDate: data.steps.property?.moveInDate,
					applicationDate: data.application.applicationDate,
				}}
				members={data.memberTimelines}
				// This page's own applicant opens pre-selected; its View
				// Application jump is suppressed (you're already here).
				defaultApplicationId={data.application.id}
				hideViewApplicationFor={data.application.id}
				memberHref={(member) =>
					`/properties/${member.propertyId ?? data.application.propertyId}/applications/${member.applicationId}/v2`
				}
			/>

			<IdVerificationModal
				open={dialog === 'id'}
				onOpenChange={openChange('id')}
				application={data.application}
				veriffDocuments={data.veriffDocuments}
				idVerification={data.idVerification}
				idVerificationStep={data.steps['id-verification']}
				aiReportData={aiReportData}
				canDownload={
					data.can.download || isDemoProperty(data.application.propertyId)
				}
				onDownload={downloadsApi.downloadIdDocuments}
				downloadBusy={Boolean(downloadsApi.busy.idDocuments)}
			/>
			<IncomeReportModal
				open={dialog === 'income'}
				onOpenChange={openChange('income')}
				aiReportData={aiReportData}
				employmentStep={data.steps.employment}
				verifyIncomeStep={data.steps['verify-income']}
				showRequestBank={data.can.requestBankAccount}
				onRequestBank={() => setDialog('bank')}
				canDownload={
					(data.can.download || isDemoProperty(data.application.propertyId)) &&
					data.downloads.incomeVerification
				}
				onDownload={(categories) =>
					downloadsApi.downloadVerificationPdf('income', categories)
				}
				downloadBusy={Boolean(downloadsApi.busy.income)}
			/>
			<RentReportModal
				open={dialog === 'rent'}
				onOpenChange={openChange('rent')}
				aiReportData={aiReportData}
				rentalStep={data.steps.rental}
				verifyIncomeStep={data.steps['verify-income']}
				showRequestBank={data.can.requestBankAccount}
				onRequestBank={() => setDialog('bank')}
				canDownload={
					(data.can.download || isDemoProperty(data.application.propertyId)) &&
					data.downloads.rentVerification
				}
				onDownload={(filters) =>
					downloadsApi.downloadVerificationPdf('rent', filters)
				}
				downloadBusy={Boolean(downloadsApi.busy.rent)}
			/>
			<CreditPreviewDialog
				open={dialog === 'credit'}
				onOpenChange={openChange('credit')}
				applicationId={data.application.id}
				propertyId={data.application.propertyId ?? ''}
				applicantName={data.application.applicantName}
			/>
			{data.billing?.canPurchaseCredits ? (
				<RenewCreditsDialog
					open={topupOpen}
					onOpenChange={setTopupOpen}
					currentBalance={data.billing.balance}
					ratio={data.billing.ratio}
					minDollars={data.billing.minTopupDollars}
				/>
			) : null}
		</>
	)
}
